/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent} from 'react';
import {makeStyles} from '@material-ui/core';
import {LabelTypographyVariantEnum} from '../../types/types';
import {LabelTypography} from '../../typography/LabelTypography';
import CheckBox, {CheckboxTypes} from '../checkbox/CheckBox';
import {IndicatorProps} from '../indicators/Indicator';
import InfoTooltip from '../tooltip/InfoTooltip';
import {FormItem} from './FormItem';
import FormLabel from './FormLabel';

export interface FormCheckBoxWithIndicatorProps extends CheckboxTypes, IndicatorProps {
  onChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  name?: string;
  disabled?: boolean;
  information?: string;
  message?: string;
};

const useStyles = makeStyles(() => ({
  checkboxWrapper : {
    display    : 'flex',
    alignItems : 'center'

  },
  info : {
    display    : 'inline-flex',
    alignItems : 'center',
    gap        : '10px'
  },
  message : {
    transform : 'translate(-7px)'
  }
}));

/**
 * @FormCheckBoxWithIndicator
 *
 * The FormCheckBoxWithIndicator component provides a customized DLS checkbox flag
 * for use in forms. This component allows for an indicator to be pre-pended to the label
 * as well as the option for an appended info icon with a tooltip message.
 *
 * ```typescript
 *
 *    import {FormCheckBoxWithIndicator} from '@ion/dls/src/components/forms/FormCheckboxWithIndicator';
 *
 *    <FormCheckBoxWithIndicator disabled={disabled}
 *      checked={checked}
 *      label={autoJobsLabel}
 *      indicator={Indicators.NEW}
 *      information={message}
 *      onChange={onChange}/>
 *
 * ```
 */
export const FormCheckBoxWithIndicator = ({
  indicator,
  information,
  label,
  message,
  ...props
}: FormCheckBoxWithIndicatorProps) => {
  const classes = useStyles();
  return (
    <FormItem fullWidth>
      <div className={classes.checkboxWrapper}>
        <CheckBox addRightMargin={!information}
          label={indicator ? (
            <div className={classes.info}>
              <FormLabel indicator={indicator}
                label={label as string} />
            </div>
          ) : label} {...props} />
        {information ? (
          <span className={classes.info}>
            <InfoTooltip message={information} />
          </span>
        ) : undefined}
      </div>
      {message ? (
        <div className={classes.message}>
          <LabelTypography variantType={LabelTypographyVariantEnum.label2}>{message}</LabelTypography>
        </div>
      ) : undefined}
    </FormItem>
  );
};

export default FormCheckBoxWithIndicator;
